import React from 'react';

/**
 * A marca do CentralMax, na identidade do hub TrackMAX.
 *
 * Vem como componente e não como <img src="/logo.svg"> de propósito: inline
 * não depende de caminho, e ainda permite trocar a paleta no tema escuro.
 *
 * Os arquivos .svg equivalentes ficam em frontend/public/ para quem precisar
 * do logo fora do sistema — apresentação, assinatura de e-mail, papelaria.
 * Mexeu aqui, mexa lá também.
 *
 * O símbolo é um giroflex: cúpula partida ao meio, azul de um lado e vermelho
 * do outro. O azul e o vermelho da TrackMAX já são as cores de uma luz de
 * emergência, então o símbolo fala de socorro sem precisar de cor nova.
 */

/** Paleta do hub, de trackmax.com.br/hub/css/main.css. */
const CLARO = {
  azulTopo: '#2A32A0', azulBase: '#0D1657',
  vermTopo: '#F04C48', vermBase: '#C62828',
  base: '#0D1657', assinatura: '#6e6e73',
};

/** O marinho some sobre preto: no escuro a marca sobe para o índigo claro. */
const ESCURO = {
  azulTopo: '#9FA8DA', azulBase: '#5C6BC0',
  vermTopo: '#FF6B67', vermBase: '#E53935',
  base: '#3949AB', assinatura: '#98989F',
};

/**
 * Ids de gradiente precisam ser únicos: em HTML o id é global no documento, e
 * dois logos na mesma página com o mesmo id fariam o segundo herdar as cores
 * do primeiro.
 *
 * O `useState` fica dentro de cada componente de propósito. Extrair para uma
 * função auxiliar viola as regras de hooks do React, que só reconhecem como
 * hook uma função cujo nome comece com `use` — e inventar um nome em inglês no
 * meio de um código em português sairia mais caro que repetir uma linha.
 */
let contador = 0;
const proximoId = () => `cmax-${(contador += 1)}`;

function Gradientes({ id, cor }) {
  return (
    <defs>
      <linearGradient id={`${id}-azul`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={cor.azulTopo} />
        <stop offset="1" stopColor={cor.azulBase} />
      </linearGradient>
      <linearGradient id={`${id}-verm`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={cor.vermTopo} />
        <stop offset="1" stopColor={cor.vermBase} />
      </linearGradient>
    </defs>
  );
}

/** O giroflex: fachos, cúpula partida e base. */
function Giroflex({ id, cor, comFachos = true }) {
  return (
    <>
      {comFachos && (
        <g strokeLinecap="round" strokeWidth="9">
          <path d="M -31.1 -21.8 L -42.6 -29.8" stroke={`url(#${id}-azul)`} />
          <path d="M -16.1 -34.4 L -22.0 -47.1" stroke={`url(#${id}-azul)`} />
          <path d="M  16.1 -34.4 L  22.0 -47.1" stroke={`url(#${id}-verm)`} />
          <path d="M  31.1 -21.8 L  42.6 -29.8" stroke={`url(#${id}-verm)`} />
        </g>
      )}
      <path d="M -30 0 A 30 30 0 0 1 0 -30 L 0 0 Z" fill={`url(#${id}-azul)`} />
      <path d="M 0 -30 A 30 30 0 0 1 30 0 L 0 0 Z" fill={`url(#${id}-verm)`} />
      <rect x="-37" y="0" width="74" height="11" rx="4" fill={cor.base} />
    </>
  );
}

const PILHA_DISPLAY =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Helvetica, Arial, sans-serif";
const PILHA_TEXTO =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Helvetica, Arial, sans-serif";

/**
 * A marca completa: giroflex, palavra e assinatura.
 * `escuro` troca a paleta para a versão de fundo escuro.
 */
export function LogoCentralMax({ escuro = false, largura = 320, className }) {
  const [id] = React.useState(proximoId);
  const cor = escuro ? ESCURO : CLARO;

  return (
    <svg
      viewBox="0 0 820 250"
      width={largura}
      height={(largura * 250) / 820}
      className={className}
      role="img"
      aria-label="CentralMax — Socorro Veicular 24 Horas"
    >
      <Gradientes id={id} cor={cor} />

      <g transform="translate(700, 74) scale(0.82)">
        <Giroflex id={id} cor={cor} />
      </g>

      <g transform="skewX(-12)">
        <rect x="52" y="52" width="404" height="15" rx="2" fill={`url(#${id}-verm)`} />
        <rect x="52" y="70" width="404" height="5" rx="2" fill={`url(#${id}-azul)`} opacity="0.55" />
        <text x="40" y="168" fontFamily={PILHA_DISPLAY} fontSize="118" fontWeight="800" letterSpacing="-3">
          <tspan fill={`url(#${id}-azul)`}>Central</tspan>
          <tspan fill={`url(#${id}-verm)`}>Max</tspan>
        </text>
      </g>

      <text x="42" y="222" fontFamily={PILHA_TEXTO} fontSize="27" fontWeight="500" letterSpacing="8.5" fill={cor.assinatura}>
        SOCORRO VEICULAR 24 HORAS
      </text>
    </svg>
  );
}

/**
 * Só o giroflex, para onde não cabe a palavra: menu recolhido, favicon, avatar.
 * Abaixo de ~24px os fachos viram sujeira, então somem e sobra a cúpula.
 */
export function SimboloCentralMax({ escuro = false, tamanho = 32, className }) {
  const [id] = React.useState(proximoId);
  const cor = escuro ? ESCURO : CLARO;
  const comFachos = tamanho >= 24;

  return (
    <svg
      viewBox="0 0 120 120"
      width={tamanho}
      height={tamanho}
      className={className}
      role="img"
      aria-label="CentralMax"
    >
      <Gradientes id={id} cor={cor} />
      <g transform={comFachos ? 'translate(60, 82) scale(1.06)' : 'translate(60, 80) scale(1.24)'}>
        <Giroflex id={id} cor={cor} comFachos={comFachos} />
      </g>
    </svg>
  );
}
